import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LoadingService } from '../../shared/services/loading.service';
import { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { Sensor, SensorDataInfo, SensorStatsSearchCriteria, SensorQueryParameterInfo, standardGetHttpOptions, standardHttpOptions, ExportDataCriteria, SensorInfo, SensorSearchCriteria, SensorReadingCriteria, SensorReadingUpdateCriteria, SensorSearchCriteria3, SensorCalibrationHistoryInfo, SensorReadingCriteria2, VirtualReadingsResult, VirtualParameterValueCriteria, VirtualParameterValueResult } from '../../shared/models';
import { of } from 'rxjs';
import { catchError, map, finalize, retry, shareReplay, tap } from 'rxjs/operators';
import { GpsInfo } from '@models/Map/gpsInfo.model';


@Injectable()
export class SensorService {
  private apiUrl: string;
  public standardHttpOptionsWithUser: any;

  // Cached variables
  public gpsLocations: GpsInfo[] = [];
  public allSensorsWithoutTypes: Sensor[] = [];
  public allSensorsWithCalibrations: Sensor[] = [];

  // Reference data that rarely changes; kept for the life of the session and intentionally not reset by clearCachedVariables()
  private sensorDatatypes: any = null;
  private sensorDatatypesInFlight: Observable<any> | null = null;
  private btMacAddressPrefixes: any = null;
  private btMacAddressPrefixesInFlight: Observable<any> | null = null;


  constructor(private http: HttpClient, private loadingService: LoadingService, @Inject(KIG_SETTINGS) private settings: KigSettings) {
    this.apiUrl = this.settings.apiUrl;
  }


  public clearCachedVariables() {
    this.gpsLocations = [];
    this.allSensorsWithoutTypes = [];
    this.allSensorsWithCalibrations = [];
  }


  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }


  public getSensorDatatypesWithIDs(): Observable<any> {
    if (this.sensorDatatypes != null) {
      // Hand out copies because callers sort the array they receive and may set fields on the items
      return of(this.sensorDatatypes.map(x => ({ ...x })));
    }

    if (this.sensorDatatypesInFlight == null) {
      this.loadingService.setMessage('Loading Sensor Data Types...');
      this.sensorDatatypesInFlight = this.http.get<any[]>(`${this.apiUrl}/sensorreading/SensorReadingType/all`, standardGetHttpOptions).pipe(
        retry(2),
        tap(response => this.sensorDatatypes = response?.map(x => ({ ...x })) ?? null),
        catchError(this.handleError),
        finalize(() => {
          this.sensorDatatypesInFlight = null;
          this.loadingService.clearMessage();
        }),
        shareReplay(1)
      );
    }
    return this.sensorDatatypesInFlight;
  }



  public getGpsLocations(): Observable<any> {
    if (this.gpsLocations.length > 0) {
      //console.log('cached gpsLocations');
      return new Observable<GpsInfo[]>((obs) => {
        obs.next(this.gpsLocations);
        obs.complete(); 
      });
    }

    this.loadingService.setMessage('Loading GPS Locations ...');
    return this.http.get(`${this.apiUrl}/sensor/gpslocations`, standardGetHttpOptions).pipe(
      map((response: GpsInfo[]) => {
        //console.log('fetched gpsLocations');
        this.gpsLocations = response;
        return this.gpsLocations;
      }),
      retry(1),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public updateSensorReadingsWithNewGpsInfo(updateCriteria: SensorReadingUpdateCriteria): Observable<any> {
    this.loadingService.setMessage('Updating Sensor Readings with GPS info ...');
    return this.http.post(`${ this.apiUrl }/sensorreading/updategpsinfo`, JSON.stringify(updateCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getAllSensorsWithoutTypes(): Observable<any> {
    if (this.allSensorsWithoutTypes.length > 0) {
      //console.log('cached allSensorsWithoutTypes');
      return new Observable<Sensor[]>((obs) => {
        obs.next(this.allSensorsWithoutTypes);
        obs.complete(); 
      });
    }

    this.loadingService.setMessage('Loading Sensors ...');
    return this.http.get(`${ this.apiUrl }/sensor`, standardGetHttpOptions).pipe(
      map((response: Sensor[]) => {
        //console.log('fetched allSensorsWithoutTypes');
        this.allSensorsWithoutTypes = response;
        return this.allSensorsWithoutTypes;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getSensorWithUid(sensorUID: string): Observable<any> {
    return this.http.get(`${ this.apiUrl }/sensor/sensorWithUid/${sensorUID}`, standardGetHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public getAllSensorsWithCalibrations(): Observable<any> {
    if (this.allSensorsWithCalibrations.length > 0) {
      //console.log('cached allSensorsWithCalibrations');
      return new Observable<Sensor[]>((obs) => {
        obs.next(this.allSensorsWithCalibrations);
        obs.complete(); 
      });
    }

    this.loadingService.setMessage('Loading Sensors ...');
    return this.http.get(`${ this.apiUrl }/sensor/withCalibrations`, standardGetHttpOptions).pipe(
      map((response: Sensor[]) => {
        //console.log('fetched allSensorsWithCalibrations');
        this.allSensorsWithCalibrations = response;
        return this.allSensorsWithCalibrations;
      }),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public captureSensorReading(sensorID: number): Observable<any> {
    this.loadingService.setMessage('Locating sensor ...');
    return this.http.get(`${ this.apiUrl }/sensor/captureSensorReading/${sensorID}`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }

  
  public blinkSensor(sensorID: number): Observable<any> {
    this.loadingService.setMessage('Locating sensor ...');
    return this.http.get(`${ this.apiUrl }/sensor/blinkSensor/${sensorID}`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public searchSensorsUsingConditions(sensorSearchCriteria: SensorSearchCriteria3): Observable<any> {
    this.loadingService.setMessage('Searching ...');
    return this.http.post(`${ this.apiUrl }/sensor/withSearch`, JSON.stringify(sensorSearchCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }

  //--------------------------------- Get Sensor Readings ---------------------------------

  public getSensorReadings(parameters: SensorQueryParameterInfo): Observable<any> {
    this.loadingService.setMessage('Loading Sensor Readings ...');
    return this.http.post(`${ this.apiUrl }/sensorreading/betweendates`, JSON.stringify(parameters), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getSensorReadingsForSensor(sensorReadingCriteria: SensorReadingCriteria2): Observable<any> {
    this.loadingService.setMessage('Loading Sensor Readings ...');
    return this.http.post(`${ this.apiUrl }/sensorreading/betweendatesforsensors`, JSON.stringify(sensorReadingCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }

  // Number of readings per reading type (all the given sensors combined) for the same criteria as
  // getSensorReadingsForSensor. A background request: no loading message, so the global overlay is not raised.
  public getReadingCountsForSensors(sensorReadingCriteria: SensorReadingCriteria2): Observable<any> {
    return this.http.post(`${ this.apiUrl }/sensorreading/betweendatesforsensors/countsbytype`, JSON.stringify(sensorReadingCriteria), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }

  //--------------------------------- Get Sensor Readings End---------------------------------

  public getSensorReadingCount(parameters: SensorQueryParameterInfo): Observable<any> {
    return this.http.post(`${ this.apiUrl }/sensorreading/betweendates/count`, JSON.stringify(parameters), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public getSensorReadingsForEvent(eventId: number, lastXHours: number): Observable<any> {
    return this.http.get(`${ this.apiUrl }/sensorreading/sensorReadingsForEvent/${eventId}/${lastXHours}`, standardGetHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public exportSensorReadings(parameters: ExportDataCriteria): Observable<any> {
    this.loadingService.setMessage('Exporting sensor data ...');
    return this.http.post(`${ this.apiUrl }/sensorreading/export`, JSON.stringify(parameters), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public exportSensorReadingsAsText(parameters: ExportDataCriteria): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    this.loadingService.setMessage('Exporting sensor data ...');
    return this.http.post(`${ this.apiUrl }/sensorreading/exportAsText`, JSON.stringify(parameters), { headers, responseType: 'text'}).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getReadingTypesForSensors(sensorIds: number[], loadMessage: string): Observable<any> {
    if (loadMessage !== null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.post(`${ this.apiUrl }/sensor/sensordatatypes`, JSON.stringify(sensorIds), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage !== null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public getSensorDataAvailability(sensorReadingCriteria: SensorReadingCriteria, loadMessage: string): Observable<any> {
    if (loadMessage !== null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.post(`${ this.apiUrl }/sensorreading/readingsAvailability`, JSON.stringify(sensorReadingCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage !== null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }

 
  public createSensor(sensorInfos: SensorInfo[]): Observable<any> {
    this.loadingService.setMessage('Creating sensor(s) ...');
    return this.http.post(`${ this.apiUrl }/sensor/`, JSON.stringify(sensorInfos), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public updateSensor(sensorInfos: SensorInfo[]) {
    this.loadingService.setMessage('Updating sensor(s) ...');
    return this.http.put(`${ this.apiUrl }/sensor/`, JSON.stringify(sensorInfos), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public deleteSensor(sensorInfo: SensorInfo, username: string) {
    this.loadingService.setMessage('Deleting sensor ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${ this.apiUrl }/sensor/${ sensorInfo.id }`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public countReadings(criteria: SensorReadingCriteria) {
    return this.http.post(`${ this.apiUrl }/sensorreading/countreadings`, JSON.stringify(criteria), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public getLastSensorReadings(sensorIds: number[]): Observable<any> {
    return this.http.post(`${ this.apiUrl }/sensorreading/lastSensorReading`, JSON.stringify(sensorIds), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  public testVirtualSensorFunction(sensorInfo: SensorInfo) {
    //this.loadingService.setMessage('Testing virtual sensor ...');
    return this.http.post(`${ this.apiUrl }/sensor/testvirtualsensor`, JSON.stringify(sensorInfo), standardHttpOptions).pipe(
      catchError(this.handleError),
    //  finalize(() => this.loadingService.clearMessage())
    );
  }


  public ingestSensorData(sensorDataInfo: SensorDataInfo) {
    return this.http.post(`${ this.apiUrl }/sensor/ingest`, JSON.stringify(sensorDataInfo), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }


  public getSensorStatsAndData(sensorParameters: SensorStatsSearchCriteria, loadMessage: string): Observable<any> {
    if (loadMessage !== null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.post(`${ this.apiUrl }/sensor/stats`, JSON.stringify(sensorParameters), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage !== null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  public showSensorAssociations(sensorIds: number[]): Observable<any> {
    this.loadingService.setMessage('Retrieving Sensor Associations');
    return this.http.post(`${ this.apiUrl }/sensor/associations`, sensorIds, standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  // TODO update Observable<any> to accurately reflect returned data type
  public getSensorsWithReadingTypesUsingSearch(searchCriteria: SensorSearchCriteria): Observable<any> {
    return this.http.post(`${ this.apiUrl }/sensor/searchAndReturnSensorswithReadingTypes`, JSON.stringify(searchCriteria), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }


  public generateNewVirtualSensorReadings(readingsCriteria: SensorReadingCriteria) {
    this.loadingService.setMessage('Generating virtual readings ...');
    return this.http.post(`${ this.apiUrl }/sensor/generatevirtualreadings`, JSON.stringify(readingsCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  // The value a virtual sensor variable would take right now from its "use last X" and aggregation settings.
  // No global loading message: the row's own icon shows the progress.
  public calculateVirtualParameterValue(criteria: VirtualParameterValueCriteria): Observable<VirtualParameterValueResult> {
    return this.http.post<VirtualParameterValueResult>(`${ this.apiUrl }/sensor/virtualparametervalue`, JSON.stringify(criteria), standardHttpOptions).pipe(
      catchError(this.handleError)
    );
  }


  // Same as generateNewVirtualSensorReadings, but the result lists every timestamp with the value created (or
  // why none was) and the stored readings it was calculated from.
  public generateNewVirtualSensorReadingsWithDetails(readingsCriteria: SensorReadingCriteria): Observable<VirtualReadingsResult> {
    this.loadingService.setMessage('Generating virtual readings ...');
    return this.http.post<VirtualReadingsResult>(`${ this.apiUrl }/sensor/generatevirtualreadings/details`, JSON.stringify(readingsCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public getBtMacAddressPrefixes(): Observable<any> {
    if (this.btMacAddressPrefixes != null) {
      return of({ ...this.btMacAddressPrefixes });
    }

    if (this.btMacAddressPrefixesInFlight == null) {
      this.btMacAddressPrefixesInFlight = this.http.get(`${ this.apiUrl }/sensor/btAddressPrefixes`, standardGetHttpOptions).pipe(
        tap(response => this.btMacAddressPrefixes = response == null ? null : { ...(response as object) }),
        catchError(this.handleError),
        finalize(() => this.btMacAddressPrefixesInFlight = null),
        shareReplay(1)
      );
    }
    return this.btMacAddressPrefixesInFlight;
  }
  

  public getReadingCountsByReadingTypeForDateRange(readingsCriteria: SensorReadingCriteria): Observable<any> {
    this.loadingService.setMessage('Get Sensor Reading Counts ...');
    return this.http.post(`${ this.apiUrl }/sensorreading/getReadingCountsByReadingType`, JSON.stringify(readingsCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public deleteSensorReadingsForReadingTypes(readingsCriteria: SensorReadingCriteria) {
    this.loadingService.setMessage('Deleting readings ...');
    return this.http.post(`${ this.apiUrl }/sensorreading/deletereadings`, JSON.stringify(readingsCriteria), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage())
    );
  }


  public addBtMacAddressPrefixes(newBtMacAddresses: { [key: string] : string }) {
    return this.http.post(`${ this.apiUrl }/sensor/btAddressPrefixes`, JSON.stringify(newBtMacAddresses), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }


  public getCalibrationHistory(sensorIds: number[], loadMessage: string): Observable<any> {
    if (loadMessage !== null && loadMessage !== '') {
      this.loadingService.setMessage(loadMessage);
    }
    return this.http.post(`${ this.apiUrl }/sensor/calibrationhistoryforsensors`, JSON.stringify(sensorIds), standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => {
        if (loadMessage !== null && loadMessage !== '') {
          this.loadingService.clearMessage();
        }
      })
    );
  }


  updateCalibrationNotes(calibrationId: number, notes: string): Observable<any> {
    return this.http.put(`${ this.apiUrl }/sensor/calibrationhistorynotes/${calibrationId}`, JSON.stringify(notes), standardHttpOptions).pipe(
      map((response: Response) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }


  updateCalibrationHistory(calibrationId: number, body: { startDate?: string, notes?: string, isActive?: boolean }, username: string): Observable<any> {
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${ this.apiUrl }/sensor/calibrationhistory/${calibrationId}`, JSON.stringify(body), this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }


  createOrUpdateCalibrationHistory(calibrationHistoryInfos: SensorCalibrationHistoryInfo[]): Observable<any> {
    return this.http.post(`${ this.apiUrl }/sensor/calibrationhistory`, JSON.stringify(calibrationHistoryInfos), standardHttpOptions).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }

  
  deleteCalibrationHistory(calibrationId: number, username: string): Observable<any> {
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${ this.apiUrl }/sensor/calibrationhistory/${calibrationId}`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError)
    );
  }

  
  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }
}
